import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Plus, Trash2, Save, AlertTriangle } from 'lucide-react';
import apiService from '../../services/apiService';



const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

const Page = styled.div`
  min-height: 100vh; background: #0a0a0f; color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif; padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #1e2030;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer;
  padding: 8px; border-radius: 8px; transition: all 0.2s;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const PageTitle = styled.h1`
  font-size: 20px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #10b981, #06b6d4);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const NavTabs = styled.div`display: flex; gap: 8px; margin-left: auto;`;
const NavTab = styled.button`
  background: ${p => p.active ? 'rgba(16,185,129,0.15)' : 'transparent'};
  border: 1px solid ${p => p.active ? '#10b981' : '#1e2030'};
  color: ${p => p.active ? '#10b981' : '#64748b'};
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  &:hover { border-color: #10b981; color: #10b981; }
`;

const Grid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  animation: ${fadeIn} 0.4s ease;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Section = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 16px;
  padding: 24px;
`;
const SectionTitle = styled.h3`
  margin: 0 0 20px; font-size: 14px; font-weight: 700; color: #94a3b8;
  display: flex; align-items: center; gap: 8px;
`;

const FormRow = styled.div`margin-bottom: 18px;`;
const Label = styled.label`
  display: block; font-size: 12px; font-weight: 600; color: #64748b;
  margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em;
`;
const Input = styled.input`
  width: 100%; background: #0d1520; border: 1px solid #1e2030; border-radius: 8px;
  color: #e2e8f0; padding: 10px 14px; font-size: 14px; outline: none; box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { border-color: #10b981; }
  &::placeholder { color: #374151; }
`;
const SubNote = styled.p`font-size: 11px; color: #475569; margin: 4px 0 0;`;

const Toggle = styled.button`
  position: relative; width: 48px; height: 26px; border-radius: 13px;
  border: none; cursor: pointer; transition: all 0.3s;
  background: ${p => p.on ? '#10b981' : '#1e2030'};
  &::after {
    content: ''; position: absolute;
    width: 20px; height: 20px; border-radius: 50%; background: #fff;
    top: 3px; left: ${p => p.on ? '25px' : '3px'}; transition: left 0.3s;
  }
`;

const WatchlistItem = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  background: #0d1520; border: 1px solid #1e2030; border-radius: 10px;
  margin-bottom: 8px;
`;
const WatchlistInfo = styled.div`flex: 1;`;
const SymbolCode = styled.div`font-size: 12px; font-weight: 700; color: #e2e8f0;`;
const SymbolName = styled.div`font-size: 11px; color: #64748b;`;
const WatchlistType = styled.span`
  font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 5px;
  background: rgba(16,185,129,0.1); color: #10b981;
`;
const DeleteBtn = styled.button`
  background: none; border: none; color: #475569; cursor: pointer; padding: 4px;
  border-radius: 6px; transition: all 0.2s;
  &:hover { color: #ef4444; background: rgba(239,68,68,0.1); }
`;

const AddRow = styled.div`
  display: flex; gap: 8px; margin-top: 12px;
`;
const SmallInput = styled.input`
  flex: 1; background: #0d1520; border: 1px solid #1e2030; border-radius: 8px;
  color: #e2e8f0; padding: 8px 12px; font-size: 13px; outline: none;
  &:focus { border-color: #10b981; }
  &::placeholder { color: #374151; }
`;
const AddBtn = styled.button`
  display: flex; align-items: center; gap: 5px;
  background: rgba(16,185,129,0.15); border: 1px solid #10b981;
  color: #10b981; padding: 8px 14px; border-radius: 8px;
  font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
  &:hover { background: rgba(16,185,129,0.25); }
`;

const SaveBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  background: linear-gradient(135deg, #10b981, #06b6d4);
  border: none; color: #fff; padding: 12px 28px; border-radius: 10px;
  font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s;
  margin-top: 24px;
  &:hover { transform: translateY(-1px); opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
`;

const DangerSection = styled.div`
  background: rgba(239,68,68,0.05); border: 1px solid rgba(239,68,68,0.3);
  border-radius: 16px; padding: 24px; margin-top: 20px;
  animation: ${fadeIn} 0.4s ease;
`;
const DangerTitle = styled.h3`
  margin: 0 0 14px; font-size: 14px; font-weight: 700; color: #ef4444;
  display: flex; align-items: center; gap: 8px;
`;
const DangerBtn = styled.button`
  background: rgba(239,68,68,0.1); border: 1px solid #ef4444; color: #ef4444;
  padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: rgba(239,68,68,0.2); }
`;

const Toast = styled.div`
  position: fixed; bottom: 24px; right: 24px; z-index: 9999;
  background: ${p => p.type === 'error' ? '#ef4444' : '#10b981'};
  color: #fff; padding: 12px 20px; border-radius: 10px;
  font-size: 13px; font-weight: 600; animation: ${fadeIn} 0.3s ease;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.7); z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(4px);
`;
const ModalContent = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 16px;
  padding: 24px; width: 90%; max-width: 400px;
  animation: ${fadeIn} 0.2s ease;
`;
const ModalTitle = styled.h3`
  margin: 0 0 12px; font-size: 16px; color: #ef4444; display: flex; align-items: center; gap: 8px;
`;
const ModalText = styled.p`
  margin: 0 0 20px; font-size: 14px; color: #e2e8f0; line-height: 1.5;
`;
const ModalActions = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
`;
const ModalBtn = styled.button`
  padding: 10px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s; border: none;
  background: ${p => p.danger ? '#ef4444' : '#1e2030'};
  color: ${p => p.danger ? '#fff' : '#e2e8f0'};
  &:hover { opacity: 0.9; }
`;

function TurtleSettingsPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [newSymbol, setNewSymbol] = useState('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('STOCK');
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      const res = await apiService.getTurtleConfig();
      setConfig(res);
    } catch (err) {
      showToast('설정 로드 실패: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateTurtleConfig(config);
      showToast('✅ 설정이 저장되었습니다');
    } catch (err) {
      showToast('저장 실패: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddWatchlist = () => {
    if (!newSymbol.trim() || !newName.trim()) {
      showToast('종목코드와 이름을 입력해주세요', 'error');
      return;
    }
    const exists = config.watchlist.some(w => w.symbol === newSymbol.trim());
    if (exists) { showToast('이미 추가된 종목입니다', 'error'); return; }
    setConfig(prev => ({
      ...prev,
      watchlist: [...prev.watchlist, { symbol: newSymbol.trim(), name: newName.trim(), type: newType }]
    }));
    setNewSymbol(''); setNewName('');
  };

  const handleRemoveWatchlist = (symbol) => {
    setConfig(prev => ({ ...prev, watchlist: prev.watchlist.filter(w => w.symbol !== symbol) }));
  };

  const handleReset = () => {
    setShowResetConfirm(true);
  };

  const executeReset = async () => {
    setShowResetConfirm(false);
    try {
      await apiService.resetTurtleAll();
      showToast('✅ 초기화 완료');
    } catch (err) {
      showToast('초기화 실패: ' + err.message, 'error');
    }
  };

  if (loading) return <Page><div style={{ textAlign: 'center', padding: 60, color: '#475569' }}>로딩 중...</div></Page>;
  if (!config) return <Page><div style={{ textAlign: 'center', padding: 60, color: '#ef4444' }}>⚠️ 설정 데이터를 불러올 수 없습니다.</div></Page>;

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/turtle')}><ArrowLeft size={18} /></BackBtn>
        <PageTitle>⚙️ 터틀 트레이딩 설정</PageTitle>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/turtle')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/multi-sim')}>멀티 시뮬</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/screener')}>스크리너</NavTab>
          <NavTab active onClick={() => navigate('/labs/turtle/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
      </TopBar>

      <Grid>
        {/* 계좌 설정 */}
        <Section>
          <SectionTitle>💰 계좌 설정</SectionTitle>
          <FormRow>
            <Label>가상 계좌 금액 (원)</Label>
            <Input type="number" value={config.accountEquity}
              onChange={e => setConfig(p => ({ ...p, accountEquity: Number(e.target.value) }))} />
            <SubNote>현재: {Number(config.accountEquity).toLocaleString()}원</SubNote>
          </FormRow>
          <FormRow>
            <Label>Unit당 위험률 (%)</Label>
            <Input type="number" step="0.1" value={(config.riskPerUnit * 100).toFixed(1)}
              onChange={e => setConfig(p => ({ ...p, riskPerUnit: Number(e.target.value) / 100 }))} />
            <SubNote>1 Unit = 계좌 {(config.riskPerUnit * 100).toFixed(1)}% 위험 (원본 규칙: 1%)</SubNote>
          </FormRow>
          <FormRow>
            <Label>최대 Unit 수 (종목당)</Label>
            <Input type="number" min="1" max="4" value={config.maxUnitsPerMarket}
              onChange={e => setConfig(p => ({ ...p, maxUnitsPerMarket: Number(e.target.value) }))} />
            <SubNote>원본 규칙: 4 Unit</SubNote>
          </FormRow>
        </Section>

        {/* 시스템 파라미터 */}
        <Section>
          <SectionTitle>📊 시스템 파라미터</SectionTitle>
          <FormRow>
            <Label>System 1 진입 (일)</Label>
            <Input type="number" value={config.system1EntryPeriod}
              onChange={e => setConfig(p => ({ ...p, system1EntryPeriod: Number(e.target.value) }))} />
            <SubNote>현재 설정: 전일까지 {config.system1EntryPeriod}일 고가 돌파 시 진입</SubNote>
          </FormRow>
          <FormRow>
            <Label>System 1 청산 (일)</Label>
            <Input type="number" value={config.system1ExitPeriod}
              onChange={e => setConfig(p => ({ ...p, system1ExitPeriod: Number(e.target.value) }))} />
            <SubNote>현재 설정: {config.system1ExitPeriod}일 저가 이탈 시 청산</SubNote>
          </FormRow>
          <FormRow>
            <Label>System 2 진입 (일)</Label>
            <Input type="number" value={config.system2EntryPeriod}
              onChange={e => setConfig(p => ({ ...p, system2EntryPeriod: Number(e.target.value) }))} />
            <SubNote>현재 설정: 전일까지 {config.system2EntryPeriod}일 고가 돌파 시 진입</SubNote>
          </FormRow>
          <FormRow>
            <Label>System 2 청산 (일)</Label>
            <Input type="number" value={config.system2ExitPeriod}
              onChange={e => setConfig(p => ({ ...p, system2ExitPeriod: Number(e.target.value) }))} />
            <SubNote>현재 설정: {config.system2ExitPeriod}일 저가 이탈 시 청산</SubNote>
          </FormRow>
          <FormRow>
            <Label>N(ATR) 계산 기간 (일)</Label>
            <Input type="number" value={config.nPeriod}
              onChange={e => setConfig(p => ({ ...p, nPeriod: Number(e.target.value) }))} />
            <SubNote>원본 규칙: 20일 EMA of True Range</SubNote>
          </FormRow>
        </Section>
      </Grid>

      {/* 감시 종목 관리 */}
      <Section style={{ marginTop: 20 }}>
        <SectionTitle>👁 감시 종목 관리</SectionTitle>
        {config.watchlist.map(w => (
          <WatchlistItem key={w.symbol}>
            <WatchlistInfo>
              <SymbolCode>{w.symbol}</SymbolCode>
              <SymbolName>{w.name}</SymbolName>
            </WatchlistInfo>
            <WatchlistType>{w.type}</WatchlistType>
            <DeleteBtn onClick={() => handleRemoveWatchlist(w.symbol)}><Trash2 size={14} /></DeleteBtn>
          </WatchlistItem>
        ))}
        <AddRow>
          <SmallInput placeholder="종목코드 (예: 069500)" value={newSymbol}
            onChange={e => setNewSymbol(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddWatchlist()} />
          <SmallInput placeholder="종목명 (예: KODEX 200)" value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddWatchlist()} />
          <select value={newType} onChange={e => setNewType(e.target.value)}
            style={{ background: '#0d1520', border: '1px solid #1e2030', color: '#e2e8f0', padding: '8px 10px', borderRadius: 8, fontSize: 13 }}>
            <option value="ETF">ETF</option>
            <option value="STOCK">주식</option>
            <option value="FUTURES">선물</option>
          </select>
          <AddBtn onClick={handleAddWatchlist}><Plus size={14} />추가</AddBtn>
        </AddRow>
        <SubNote style={{ marginTop: 10 }}>
          기본 제공: KODEX 200 (069500), KODEX 코스피200 (229200) — KOSPI200 선물 기준 ETF
        </SubNote>
      </Section>

      {/* 스케줄러 설정 */}
      <Section style={{ marginTop: 20 }}>
        <SectionTitle>⏰ 스케줄러 설정</SectionTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Toggle on={config.isActive ? 1 : 0}
            onClick={() => setConfig(p => ({ ...p, isActive: !p.isActive }))} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              자동매매 {config.isActive ? '활성화' : '비활성화'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
              매일 평일 15:20 (장 마감 직전) 자동 신호 스캔 실행
            </div>
          </div>
        </div>
      </Section>

      <SaveBtn onClick={handleSave} disabled={saving}>
        <Save size={16} />
        {saving ? '저장 중...' : '설정 저장'}
      </SaveBtn>

      {/* 위험 구역 */}
      <DangerSection>
        <DangerTitle><AlertTriangle size={16} />위험 구역</DangerTitle>
        <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 16px' }}>
          아래 작업은 취소할 수 없습니다. 신중하게 실행하세요.
        </p>
        <DangerBtn onClick={handleReset}>
          🔄 모든 오픈 포지션 초기화
        </DangerBtn>
        <SubNote style={{ marginTop: 8, color: '#64748b' }}>
          거래 일지와 수익률 이력은 유지됩니다. 오픈 포지션만 RESET 상태로 변경됩니다.
        </SubNote>
      </DangerSection>

      {showResetConfirm && (
        <ModalOverlay onClick={() => setShowResetConfirm(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle><AlertTriangle size={18} />초기화 경고</ModalTitle>
            <ModalText>
              모든 오픈 포지션, 거래 일지, 수익률 이력이 <b>완전히 삭제</b>됩니다.<br/>
              이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?
            </ModalText>
            <ModalActions>
              <ModalBtn onClick={() => setShowResetConfirm(false)}>취소</ModalBtn>
              <ModalBtn danger onClick={executeReset}>네, 초기화합니다</ModalBtn>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {toast && <Toast type={toast.type}>{toast.msg}</Toast>}
    </Page>
  );
}

export default TurtleSettingsPage;
